import { useEffect } from 'react'
import {
  SimpleGrid,
  Card,
  Text,
  Title,
  Badge,
  Button,
  Group,
  Avatar,
  Stack,
  Center,
  Loader,
  Alert,
} from '@mantine/core'
import { IconApps, IconAlertCircle, IconExternalLink, IconLayoutDashboard } from '@tabler/icons-react'
import { SYSTEM_APP_IDS } from '@paperair/shared-types'
import { useNavigate } from 'react-router'
import { useLauncherStore } from '../../stores/launcherStore'
import type { UserApp } from '../../api/launcherApi'
import { PasskeySetupBanner } from './PasskeySetupBanner'

function roleBadgeColor(role: string): string {
  switch (role.toLowerCase()) {
    case 'admin': return 'red'
    case 'manager': return 'blue'
    case 'user': return 'green'
    default: return 'gray'
  }
}

function appInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const APP_COLORS = ['blue', 'teal', 'violet', 'orange', 'cyan', 'pink', 'grape', 'indigo']

function appColor(appId: string): string {
  let hash = 0
  for (let i = 0; i < appId.length; i++) hash = appId.charCodeAt(i) + ((hash << 5) - hash)
  return APP_COLORS[Math.abs(hash) % APP_COLORS.length]
}

interface AppCardProps {
  app: UserApp
  onOpen: (app: UserApp) => void
  isLaunching: boolean
}

function AppCard({ app, onOpen, isLaunching }: AppCardProps) {
  const isAdminApp = app.appId === SYSTEM_APP_IDS.AUTH_ADMIN

  return (
    <Card withBorder radius="md" padding="lg" style={{ display: 'flex', flexDirection: 'column' }}>
      <Stack gap="md" style={{ flex: 1 }}>
        <Group>
          <Avatar size="xl" radius="md" color={appColor(app.appId)} src={`/app-icons/${app.appId}.png`}>
            {appInitials(app.name)}
          </Avatar>
          <Stack gap={2} style={{ flex: 1 }}>
            <Text fw={600} size="sm" lineClamp={1}>
              {app.name}
            </Text>
            <Badge size="xs" variant="light" color={roleBadgeColor(app.role)}>
              {app.role}
            </Badge>
          </Stack>
        </Group>

        {app.description && (
          <Text size="sm" c="dimmed" lineClamp={2} style={{ flex: 1 }}>
            {app.description}
          </Text>
        )}
      </Stack>

      <Button
        mt="md"
        variant="light"
        fullWidth
        loading={isLaunching}
        leftSection={isAdminApp ? <IconLayoutDashboard size={16} /> : <IconExternalLink size={16} />}
        onClick={() => onOpen(app)}
      >
        {isAdminApp ? 'Open Dashboard' : 'Open'}
      </Button>
    </Card>
  )
}

export function LauncherView() {
  const navigate = useNavigate()
  const { apps, isLoading, error, fetchApps, launchApp, lastOpenedAppId } = useLauncherStore()

  useEffect(() => {
    fetchApps()
  }, [fetchApps])

  useEffect(() => {
    // Auto-redirect when user has exactly one app and fetch is done
    if (!isLoading && apps.length === 1) {
      launchApp(apps[0].appId, navigate)
    }
  }, [isLoading, apps, navigate, launchApp])

  const handleOpen = (app: UserApp) => {
    launchApp(app.appId, navigate)
  }

  if (isLoading) {
    return (
      <Center style={{ minHeight: 300 }}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed" size="sm">Loading your applications…</Text>
        </Stack>
      </Center>
    )
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" title="Could not load applications">
        {error}
      </Alert>
    )
  }

  if (apps.length === 0) {
    return (
      <Center style={{ minHeight: 300 }}>
        <Stack align="center" gap="md">
          <IconApps size={48} color="var(--mantine-color-dimmed)" />
          <Title order={3} ta="center">No applications available</Title>
          <Text c="dimmed" ta="center" maw={400}>
            You don't have access to any applications yet. Contact your administrator to request access.
          </Text>
        </Stack>
      </Center>
    )
  }

  // Sort: last opened first, then alphabetically
  const sorted = [...apps].sort((a, b) => {
    if (a.appId === lastOpenedAppId) return -1
    if (b.appId === lastOpenedAppId) return 1
    return a.name.localeCompare(b.name)
  })

  return (
    <Stack gap="xl">
      <PasskeySetupBanner />
      <Group gap="md" align="center">
        <img src="/brand/logo.png" alt="PaperAirplane Platform" height={60} />
        <Stack gap={4}>
          <Title order={2}>Your Applications</Title>
          <Text c="dimmed" size="sm">Select an application to get started.</Text>
        </Stack>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }}>
        {sorted.map((app) => (
          <AppCard
            key={app.appId}
            app={app}
            onOpen={handleOpen}
            isLaunching={false}
          />
        ))}
      </SimpleGrid>
    </Stack>
  )
}
